/**
 * CallCenter — central call history page ("/appels").
 *
 * Lists every call across every candidate, with inline audio playback,
 * Deepgram transcript, Claude AI summary, and filters by direction /
 * status. Admins see calls from the whole team; other roles only see
 * their own.
 */
import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowUpRight,
    Link2,
    Loader2,
    PhoneIncoming,
    PhoneOutgoing,
    RefreshCw,
    Search,
    Sparkles,
    Trash2,
    UserPlus,
    Voicemail,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import ConfirmModal from "../components/common/ConfirmModal";
import LinkCallToCandidateDialog from "../components/call/LinkCallToCandidateDialog";
import SaveContactDialog from "../components/call/SaveContactDialog";
import { api, API_BASE, tokenStore } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDateTime } from "../lib/format";
import { hasAnyRole } from "../lib/roles";


const STATUS_LABEL = {
    completed: "Terminé",
    "no-answer": "Pas de réponse",
    busy: "Occupé",
    failed: "Échec",
    canceled: "Annulé",
    voicemail: "Messagerie",
    ringing: "Sonnerie",
    "in-progress": "En cours",
    queued: "En attente",
};
const SENTIMENT_META = {
    positive: { label: "Positif", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    neutral: { label: "Neutre", tone: "bg-slate-50 text-slate-600 border-slate-200" },
    negative: { label: "Vigilance", tone: "bg-amber-50 text-amber-800 border-amber-200" },
};


function DirectionIcon({ direction, voicemail }) {
    if (voicemail) return <Voicemail className="h-4 w-4 text-amber-600" />;
    if (direction === "inbound") return <PhoneIncoming className="h-4 w-4 text-emerald-600" />;
    return <PhoneOutgoing className="h-4 w-4 text-shariti-orange" />;
}


function formatDuration(sec) {
    if (!Number.isFinite(sec) || sec <= 0) return "—";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${String(s).padStart(2, "0")}s`;
}


/**
 * Inline audio row with lazy blob loading (auth-gated stream).
 */
function InlineAudio({ callLogId }) {
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const urlRef = useRef(null);

    const load = useCallback(async () => {
        if (src || loading) return;
        setLoading(true);
        setError(null);
        try {
            const token = tokenStore.get();
            const res = await fetch(`${API_BASE}/twilio/calls/${callLogId}/recording`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            urlRef.current = url;
            setSrc(url);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [callLogId, loading, src]);

    useEffect(() => () => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    }, []);

    if (error) return <div className="text-xs text-shariti-red">{error}</div>;
    if (!src) {
        return (
            <button
                type="button"
                onClick={load}
                data-testid={`call-center-load-audio-${callLogId}`}
                disabled={loading}
                className="h-8 inline-flex items-center gap-1.5 rounded-full bg-shariti-black hover:bg-slate-800 text-shariti-yellow px-3 text-xs font-bold transition disabled:opacity-50"
            >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span>▶ Charger l&apos;audio</span>}
            </button>
        );
    }
    return <audio controls src={src} preload="metadata" className="w-full max-w-xs rounded-lg" data-testid={`call-center-audio-${callLogId}`} />;
}


function CallRow({ call, isAdmin, canCall, onRequestDelete, onReprocess, deletingId, onSaveContact, onLinkCandidate }) {
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(null); // "reprocess"
    const sentiment = SENTIMENT_META[call.ai_sentiment];
    const hasContent = call.transcript || call.ai_summary;
    const isDeleting = deletingId === call.id;

    const handleDelete = (e) => {
        e.stopPropagation();
        onRequestDelete?.(call);
    };

    const handleReprocess = async (e) => {
        e.stopPropagation();
        setBusy("reprocess");
        try {
            await onReprocess(call.id);
        } finally {
            setBusy(null);
        }
    };

    return (
        <motion.div
            layout
            data-testid={`call-center-row-${call.id}`}
            className="rounded-xl bg-white border border-slate-200 hover:border-shariti-orange/30 p-4 transition"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-shariti-cream flex items-center justify-center shrink-0">
                        <DirectionIcon direction={call.direction} voicemail={call.voicemail} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-extrabold text-shariti-black">
                                {call.voicemail ? "Messagerie vocale" : call.direction === "inbound" ? "Appel entrant" : "Appel sortant"}
                            </span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                · {STATUS_LABEL[call.status] || call.status}
                            </span>
                            {sentiment && (
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${sentiment.tone}`}>
                                    {sentiment.label}
                                </span>
                            )}
                        </div>
                        {/* Candidate identification line — CEO 2026-07-27:
                            "je dois voir en un coup d'œil QUI on a appelé et
                            à QUEL numéro". So we ALWAYS show the phone
                            (never hidden by the name lookup) AND, when we
                            have a candidate match, the name is displayed
                            prominently as a bold pill just before it. */}
                        <div
                            className="text-xs font-semibold text-slate-700 mt-0.5 flex items-center gap-2 flex-wrap"
                            data-testid={`call-center-row-identity-${call.id}`}
                        >
                            {call.candidate_name ? (
                                call.candidate_id ? (
                                    <Link
                                        to={`/candidats/${call.candidate_id}`}
                                        className="font-black text-shariti-black hover:text-shariti-orange transition inline-flex items-center gap-1"
                                        data-testid={`call-center-row-candidate-${call.id}`}
                                    >
                                        {call.candidate_name}
                                    </Link>
                                ) : (
                                    <span className="font-black text-shariti-black">
                                        {call.candidate_name}
                                    </span>
                                )
                            ) : (
                                <span
                                    className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1.5 py-0.5 rounded-full bg-slate-100"
                                    title="Aucun candidat associé — numéro composé manuellement ou candidat supprimé"
                                >
                                    Candidat inconnu
                                </span>
                            )}
                            <span className="text-slate-400">·</span>
                            <span
                                className="font-mono text-shariti-black"
                                data-testid={`call-center-row-phone-${call.id}`}
                            >
                                {call.direction === "inbound"
                                    ? (call.from_number || "—")
                                    : (call.to_number || "—")}
                            </span>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500">{formatDateTime(call.created_at)}</span>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500">{formatDuration(call.duration_sec || call.recording_duration_sec)}</span>
                        </div>
                        {call.user_name && (
                            <div className="text-[11px] text-slate-500 mt-0.5">Recruteur : <strong>{call.user_name}</strong></div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {call.candidate_id && (
                        <Link
                            to={`/candidats/${call.candidate_id}`}
                            className="h-8 inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-shariti-orange hover:text-white text-slate-700 px-3 text-[11px] font-bold transition"
                            data-testid={`call-center-open-profile-${call.id}`}
                        >
                            Fiche <ArrowUpRight className="h-3 w-3" />
                        </Link>
                    )}
                    {/* iter94 · Save/link buttons — visible when the call has
                        no candidate attached AND the recruiter has the phone
                        (i.e. outbound calls always have to_number, inbound
                        always have from_number). */}
                    {!call.candidate_id && canCall && (
                        <>
                            <button
                                type="button"
                                onClick={() => onSaveContact?.(call)}
                                data-testid={`call-center-save-contact-${call.id}`}
                                title="Enregistrer ce numéro comme nouveau candidat"
                                className="h-8 inline-flex items-center gap-1 rounded-full bg-shariti-orange/10 hover:bg-shariti-orange hover:text-white text-shariti-orange px-3 text-[11px] font-bold transition border border-shariti-orange/30"
                            >
                                <UserPlus className="h-3 w-3" /> Enregistrer
                            </button>
                            <button
                                type="button"
                                onClick={() => onLinkCandidate?.(call)}
                                data-testid={`call-center-link-candidate-${call.id}`}
                                title="Associer cet appel à un candidat existant"
                                className="h-8 inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-shariti-black hover:text-shariti-yellow text-slate-700 px-3 text-[11px] font-bold transition"
                            >
                                <Link2 className="h-3 w-3" /> Associer
                            </button>
                        </>
                    )}
                    {isAdmin && call.recording_url && !call.ai_summary && (
                        <button
                            type="button"
                            onClick={handleReprocess}
                            disabled={busy === "reprocess"}
                            data-testid={`call-center-reprocess-${call.id}`}
                            title="Retraiter (transcription + résumé IA)"
                            className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-slate-100 hover:bg-amber-100 text-slate-500 hover:text-amber-700 transition disabled:opacity-40"
                        >
                            {busy === "reprocess" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            data-testid={`call-center-delete-${call.id}`}
                            title="Supprimer cet appel (admin)"
                            className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-700 transition disabled:opacity-40"
                        >
                            {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                    )}
                </div>
            </div>

            {call.recording_url && (
                <div className="mt-3">
                    <InlineAudio callLogId={call.id} />
                </div>
            )}

            {call.ai_summary && (
                <div className="mt-3 rounded-lg bg-shariti-orange-soft/40 border border-shariti-orange/20 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1">
                        <Sparkles className="h-3 w-3" /> Résumé IA
                    </div>
                    <div className="text-sm text-shariti-black leading-relaxed">{call.ai_summary}</div>
                </div>
            )}

            {hasContent && call.transcript && (
                <div className="mt-2">
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`call-center-toggle-transcript-${call.id}`}
                        className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-shariti-orange"
                    >
                        {expanded ? "Masquer la transcription" : "Voir la transcription"}
                    </button>
                    {expanded && (
                        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 leading-relaxed max-h-64 overflow-y-auto whitespace-pre-line">
                            {call.transcript}
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
}


export default function CallCenter() {
    const { user } = useAuth();
    const isAdmin = user && hasAnyRole(user, "admin");
    const canCall = user && hasAnyRole(user, "admin", "pre_recruteur");
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [direction, setDirection] = useState("all");
    const [status, setStatus] = useState("all");
    const [query, setQuery] = useState("");
    const [pendingDelete, setPendingDelete] = useState(null); // call object or null
    const [deletingId, setDeletingId] = useState(null);
    // iter94 · Save/Link contact from Centre d'appels
    const [saveContactCall, setSaveContactCall] = useState(null); // call object or null
    const [linkCall, setLinkCall] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/twilio/calls");
            setCalls(Array.isArray(data) ? data : []);
        } catch (_e) {
            setCalls([]);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const requestDelete = useCallback((call) => {
        setPendingDelete(call);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        const id = pendingDelete.id;
        setDeletingId(id);
        try {
            await api.delete(`/twilio/calls/${id}`);
            setCalls((cs) => cs.filter((c) => c.id !== id));
            toast.success("Appel supprimé");
            setPendingDelete(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Suppression impossible");
        } finally {
            setDeletingId(null);
        }
    }, [pendingDelete]);

    const handleReprocess = useCallback(async (id) => {
        try {
            await api.post(`/twilio/calls/${id}/reprocess`);
            toast.info("Retraitement lancé — la transcription et le résumé IA arrivent dans ~20 s");
            // Poll a few times
            for (let i = 0; i < 6; i += 1) {
                await new Promise((r) => setTimeout(r, 5000));
                await load();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retraitement impossible");
        }
    }, [load]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        // Also normalise the query digits so "0162290954" matches
        // "+33162290954" (stored in E.164) and vice versa.
        const qDigits = query.replace(/\D+/g, "");
        // Strip a leading French "0" so "0612345678" → "612345678"
        // matches the suffix of "+33612345678" (digits: "33612345678").
        const qCore = qDigits.length >= 10 && qDigits.startsWith("0")
            ? qDigits.slice(1)
            : qDigits;
        return calls.filter((c) => {
            if (direction !== "all" && c.direction !== direction) return false;
            if (status !== "all") {
                if (status === "recorded" && !c.recording_url) return false;
                if (status === "voicemail" && !c.voicemail) return false;
                if (status === "missed" && !["no-answer", "busy", "failed", "canceled"].includes(c.status)) return false;
            }
            if (q) {
                const hay = `${c.candidate_name || ""} ${c.from_number || ""} ${c.to_number || ""} ${c.transcript || ""} ${c.ai_summary || ""}`.toLowerCase();
                if (hay.includes(q)) return true;
                // Fallback: digit-only match on either number (handles
                // formatted vs raw phone entry — "01 62 29 09 54" or
                // "0612345678" typed by the user still match "+33162290954"
                // / "+33612345678" stored in E.164).
                if (qCore.length >= 4) {
                    const nums = `${c.from_number || ""} ${c.to_number || ""}`.replace(/\D+/g, "");
                    if (nums.includes(qCore)) return true;
                }
                return false;
            }
            return true;
        });
    }, [calls, direction, status, query]);

    return (
        <div className="space-y-6" data-testid="call-center-page">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl md:text-4xl font-black text-shariti-black tracking-tight">Centre d&apos;appels</h1>
                    <p className="text-sm font-semibold text-slate-500 mt-1">
                        Historique complet — enregistrements audio, transcriptions Deepgram et résumés IA.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Appels au total</div>
                    <div className="text-3xl font-black text-shariti-orange">{calls.length}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white border border-slate-200 p-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher (candidat, numéro, transcription…)"
                        data-testid="call-center-search"
                        className="w-full pl-9 pr-3 py-2 rounded-full bg-slate-50 border border-slate-200 focus:border-shariti-orange focus:ring-0 text-sm"
                    />
                </div>
                <select
                    value={direction}
                    onChange={(e) => setDirection(e.target.value)}
                    data-testid="call-center-filter-direction"
                    className="px-3 py-2 rounded-full bg-slate-50 border border-slate-200 focus:border-shariti-orange focus:ring-0 text-sm font-bold"
                >
                    <option value="all">Tous sens</option>
                    <option value="outbound">Sortants</option>
                    <option value="inbound">Entrants</option>
                </select>
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid="call-center-filter-status"
                    className="px-3 py-2 rounded-full bg-slate-50 border border-slate-200 focus:border-shariti-orange focus:ring-0 text-sm font-bold"
                >
                    <option value="all">Tous statuts</option>
                    <option value="recorded">Avec enregistrement</option>
                    <option value="voicemail">Messagerie</option>
                    <option value="missed">Manqués</option>
                </select>
            </div>

            {/* List */}
            {loading ? (
                <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Chargement de l&apos;historique…
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl bg-white border border-dashed border-slate-200 p-10 text-center">
                    <div className="text-sm font-bold text-slate-500">
                        {calls.length === 0
                            ? "Aucun appel enregistré pour le moment."
                            : "Aucun appel ne correspond aux filtres."}
                    </div>
                </div>
            ) : (
                <div className="space-y-2" data-testid="call-center-list">
                    {filtered.map((call) => (
                        <CallRow
                            key={call.id}
                            call={call}
                            isAdmin={isAdmin}
                            canCall={canCall}
                            onRequestDelete={requestDelete}
                            onReprocess={handleReprocess}
                            deletingId={deletingId}
                            onSaveContact={setSaveContactCall}
                            onLinkCandidate={setLinkCall}
                        />
                    ))}
                </div>
            )}

            {/* iter94 · Save contact from a call — reuses DialerFAB dialog. */}
            {saveContactCall && (
                <SaveContactDialog
                    open={true}
                    onOpenChange={(v) => { if (!v) setSaveContactCall(null); }}
                    phone={
                        // iter94-fix : direction-aware. Pour un inbound
                        // c'est le from_number (l'appelant), pas le
                        // to_number qui est notre ligne Shariti.
                        saveContactCall.direction === "inbound"
                            ? (saveContactCall.from_number || saveContactCall.to_number || "")
                            : (saveContactCall.to_number || saveContactCall.from_number || "")
                    }
                    callLogId={saveContactCall.id}
                    onSaved={(cand) => {
                        setSaveContactCall(null);
                        toast.success(`✅ ${cand.first_name} ${cand.last_name} enregistré · appel lié`);
                        // Optimistic update ONLY if the backend confirmed the linkage.
                        // Sans linked_call_log_id, l'appel reste en "CANDIDAT INCONNU"
                        // au reload — on doit refléter la vérité serveur.
                        if (cand.linked_call_log_id) {
                            setCalls((cs) => cs.map((c) => (c.id === saveContactCall.id
                                ? { ...c, candidate_id: cand.id, candidate_name: `${cand.first_name} ${cand.last_name}` }
                                : c
                            )));
                        }
                    }}
                />
            )}
            {/* iter94 · Link an existing candidate to a "CANDIDAT INCONNU" call. */}
            {linkCall && (
                <LinkCallToCandidateDialog
                    open={true}
                    onOpenChange={(v) => { if (!v) setLinkCall(null); }}
                    callLog={linkCall}
                    onLinked={(res) => {
                        setLinkCall(null);
                        setCalls((cs) => cs.map((c) => (c.id === linkCall.id
                            ? { ...c, candidate_id: res.candidate_id, candidate_name: res.candidate_name }
                            : c
                        )));
                    }}
                />
            )}

            {pendingDelete && (
                <ConfirmModal
                    testid="call-center-delete-modal"
                    tone="red"
                    icon={AlertTriangle}
                    title="Supprimer cet appel ?"
                    body={
                        <div className="space-y-2">
                            <p>
                                Cette action est <strong>définitive</strong>. L&apos;enregistrement audio hébergé chez
                                Twilio sera lui aussi supprimé.
                            </p>
                            <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs">
                                <div className="font-black text-shariti-black">
                                    {pendingDelete.direction === "inbound" ? "Appel entrant" : "Appel sortant"}
                                    {pendingDelete.voicemail ? " · Messagerie" : ""}
                                </div>
                                <div className="text-slate-600 mt-0.5">
                                    {pendingDelete.candidate_name || pendingDelete.from_number || pendingDelete.to_number || "—"}
                                </div>
                                <div className="text-slate-500 mt-0.5">
                                    {formatDateTime(pendingDelete.created_at)}
                                    {" · "}
                                    {formatDuration(pendingDelete.duration_sec || pendingDelete.recording_duration_sec)}
                                </div>
                            </div>
                        </div>
                    }
                    confirmLabel={deletingId ? "Suppression…" : "Supprimer l'appel"}
                    cancelLabel="Annuler"
                    onConfirm={deletingId ? () => {} : confirmDelete}
                    onCancel={deletingId ? () => {} : () => setPendingDelete(null)}
                />
            )}
        </div>
    );
}
